import {
  Box,
  useTheme,
  Typography,
  Button,
  Popper,
  ClickAwayListener,
  Tooltip,
} from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import ExoAvatar from '../ExoAvatar';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmailIcon from '@mui/icons-material/Email';
import CallIcon from '@mui/icons-material/Call';
import { Link, useNavigate } from 'react-router-dom';
import { hasPermissions } from '../../../auth/hasPermissions';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PhoneIcon from '@mui/icons-material/Phone';
import { useEmailClient } from '../../email-system/useEmailClient';
import StarIcon from '@mui/icons-material/Star';
import { NewEmail } from '../../new/NewEmail';
import ExoDialog from '../ExoDialog';
import LaunchIcon from '@mui/icons-material/Launch';

const ProjectParticipantCard = ({ participantData }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const { emailCredentials } = useEmailClient();

  const participant = participantData.participant;

  const navigate = useNavigate();
  // check if its company lead
  const isCompanyLead = participantData.type === 'companyLead';

  // call
  const [callAnchorEl, setCallAnchorEl] = useState(null);
  function handleOpenNewCall(e) {
    setCallAnchorEl(e.currentTarget);
  }

  // mail
  const [mailAnchorEl, setMailAnchorEl] = useState(null);
  function handleOpenNewMail(e) {
    setMailAnchorEl(e.currentTarget);
  }

  // write email address to start data collection
  const [startData, setStartData] = useState(null);
  function handleEmailSelect(email) {
    setStartData({
      to: [
        {
          type: isCompanyLead ? 'companyLead' : 'personLead',
          email,
          participant: participant,
        },
      ],
    });
  }

  return (
    <Box
      className="flex items-center w-full p-2 rounded-lg flex-row gap-3 transition-transform backdrop-blur-lg"
      sx={{
        backgroundColor: colors.card,
        cursor: 'default',
        border: `1px solid transparent`,
      }}
    >
      <ExoAvatar
        picture={participant.logo ? participant.logo.temporaryUrl : ''}
        name={participant.firstName + ' ' + participant.lastName}
        icon={participant?.icon}
        size="35"
        onClick={() =>
          navigate(
            isCompanyLead
              ? `/leads/companies/${participantData.participant.id}`
              : `/leads/persons/${participantData.participant.id}`
          )
        }
      />
      <Box className="flex justify-start items-center">
        <Box>
          <Box className="flex items-center gap-2">
            <Typography fontSize="14px" fontWeight={600}>
              {isCompanyLead
                ? participantData.participant.name
                : `${participantData.participant?.firstName} ${participantData.participant?.lastName}`}
            </Typography>
          </Box>

          <Typography fontSize="12px" sx={{ color: colors.primary[600] }}>
            {participant?.position}
          </Typography>
          <Box className="flex items-stretch gap-1 py-1">
            <ContactButton
              Icon={CallIcon}
              label={t('Call')}
              onClick={handleOpenNewCall}
            />
            <ContactButton
              Icon={EmailIcon}
              label={t('E-Mail')}
              onClick={handleOpenNewMail}
            />
          </Box>
        </Box>
      </Box>

      <Popper open={Boolean(callAnchorEl)} anchorEl={callAnchorEl}>
        <ClickAwayListener onClickAway={() => setCallAnchorEl(null)}>
          <Box
            className="px-4 py-2 rounded-lg flex flex-col gap-1 backdrop-blur-md "
            sx={{
              background: `linear-gradient(135deg,  ${colors.glassFrom}, ${colors.glassTo})`,
              border: `1px solid ${colors.selected}`,
              maxHeight: 500,
              overflowY: 'auto',
            }}
          >
            <Typography fontWeight={600}>{t('Phonenumbers')}</Typography>
            {participant?.telephoneNumbers?.length > 0 ? (
              participant.telephoneNumbers.map((number) => (
                <Link href={`tel:${number.phoneNumber}`} key={number.id}>
                  <Button
                    sx={{
                      border: `1px solid transparent`,
                      ':hover': {
                        bgcolor: colors.info + '30',
                        border: `1px solid ${colors.info}`,
                      },
                    }}
                  >
                    <Typography className=" items-center flex">
                      <PhoneIcon className="mr-3" />
                      <b>{number.phoneType}</b>
                      <ChevronRightIcon className="mx-1" />
                      {number.phoneNumber}
                    </Typography>
                  </Button>
                </Link>
              ))
            ) : (
              <Typography
                fontSize="12px"
                sx={{ color: colors.text }}
                className="opacity-50"
              >
                {t('No phone numbers for this participant')}
              </Typography>
            )}
          </Box>
        </ClickAwayListener>
      </Popper>
      <Popper open={Boolean(mailAnchorEl)} anchorEl={mailAnchorEl}>
        <ClickAwayListener onClickAway={() => setMailAnchorEl(null)}>
          <Box
            className="px-4 py-2 rounded-lg flex flex-col gap-1 backdrop-blur-md"
            sx={{
              background: `linear-gradient(135deg,  ${colors.glassFrom}, ${colors.glassTo})`,
              border: `1px solid ${colors.selected}`,
              maxHeight: 500, // set max height as needed
              overflowY: 'auto',
            }}
          >
            <Typography fontWeight={600}>{t('E-Mails')}</Typography>
            {participant?.emails?.map((element) => (
              <Link
                href={
                  hasPermissions('email-inbox') &&
                  emailCredentials?.data?.length > 0
                    ? undefined
                    : `mailto:${element.emailAddress}`
                }
                key={element.id}
              >
                <Button
                  sx={{
                    border: `1px solid transparent`,
                    ':hover': {
                      bgcolor: colors.info + '30',
                      border: `1px solid ${colors.info}`,
                    },
                  }}
                  onClick={
                    hasPermissions('email-inbox') &&
                    emailCredentials?.data?.length > 0
                      ? () => handleEmailSelect(element.emailAddress)
                      : undefined
                  }
                >
                  <Typography className=" items-center flex">
                    <EmailIcon className="mr-3" />
                    {element.isPrimary && (
                      <StarIcon
                        fontSize="small"
                        sx={{ color: colors.warning, mr: 1 }}
                      />
                    )}
                    <b>{element.emailType}</b>
                    <ChevronRightIcon className="mx-1" />
                    {element.emailAddress}
                  </Typography>
                </Button>
              </Link>
            ))}
          </Box>
        </ClickAwayListener>
      </Popper>
      <ExoDialog open={Boolean(startData)} onClose={() => setStartData(null)}>
        <NewEmail startData={startData} onNewMail={() => setStartData(null)} />
      </ExoDialog>
    </Box>
  );
};

export default ProjectParticipantCard;

const ContactButton = ({ onClick, Icon, label }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <Button
      color="secondary"
      variant="contained"
      disableElevation
      onClick={onClick}
      sx={{
        bgcolor: colors.bgInput,
        ':hover': {
          bgcolor: colors.selected,
        },
        padding: '0px!important',
      }}
    >
      <Box className="h-full w-full flex items-center justify-center gap-1 px-2 py-1">
        <Icon
          sx={{
            fontSize: '20px',
          }}
        />
        <Typography fontWeight={500} fontSize="10px">
          {label}
        </Typography>
      </Box>
    </Button>
  );
};
